import {
  Card,
  CardBody,
  CardProps,
  Flex,
  LinkBox,
  LinkOverlay,
  Image,
  Divider,
  IconButton,
  HStack,
  Tag,
} from "@chakra-ui/react";
import CDS from "design-system";
import ButtonWithConfirm from "components/Button/ButtonWithConfirm";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { useDeleteIntegration, useToggleActiveIntegration } from "features/Integrations/hooks";
import { PropsWithChildren } from "react";
import { generatePath, Link, useNavigate } from "react-router";
import { timeAtDayMonth } from "helpers/time";
import { IoMdPause, IoMdPlay } from "react-icons/io";
import { DeleteIcon } from "@chakra-ui/icons";
import { errorStyles, greenStyles, orangeStyles } from "components/Tag/Status/styles";
import { Heading20 } from "components/Heading";
import { TertiaryButton } from "components/Button";
import SyncChannelsButton from "./SyncChannelsButton";

const CONFIRM_DELETE = {
  header: "Remove Integration",
  body: "Are you sure you'd like to remove the credentials for this integration?",
  confirmButtonText: "Yes, remove",
};

interface IntegrationCardProps extends CardProps, PropsWithChildren {
  id?: string;
  name: string;
  partner: string;
  description: string;
  logoUrl?: string;
  isActive?: boolean;
  isConnected?: boolean;
  needsReauth?: boolean;
  includeDelete?: boolean;
  pauseable?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  onConnect?: () => void;
  connectButtonText?: string;
  messageService?: {
    channelsLastSyncedAt?: Date;
    usersLastSyncedAt?: Date;
    url?: string;
  };
}

export default function IntegrationCard({
  id,
  name,
  partner,
  description,
  logoUrl,
  isActive = undefined,
  isConnected = false,
  needsReauth = false,
  includeDelete = false,
  pauseable = false,
  createdAt,
  updatedAt,
  onConnect,
  connectButtonText = "Connect",
  messageService,
  ...rest
}: IntegrationCardProps) {
  const { performRequest: handleDelete } = useDeleteIntegration(id || "");
  const { performRequest: toggleIsActive } = useToggleActiveIntegration(id || "", isActive || false);
  const navigate = useNavigate();

  const isSlack = partner === "slack";
  const showSyncChannels = isSlack && isConnected;
  const needsReconnect = isConnected && needsReauth;
  const showPauseButton = pauseable && !isSlack && !needsReconnect;
  const statusStyle = needsReconnect ? errorStyles : isActive ? greenStyles : orangeStyles;
  const statusLabel = needsReconnect ? "Needs reconnection" : isActive ? "Active" : "Paused";

  const handleReconnect = () => navigate(generatePath(PEOPLE_TEAM_ROUTES.company.integrations.show, { id: id || "" }));

  return (
    <LinkBox
      as={Card}
      minWidth="380px"
      maxWidth="380px"
      _hover={{ bg: "bg.tertiary" }}
      {...rest}
    >
      <CardBody height="100%" display="flex" flexDirection="column" p={6}>
        <Flex alignItems="center" justifyContent="space-between" mb={4}>
          <HStack spacing={3} flex={1}>
            {logoUrl && (
              <Image
                src={logoUrl}
                alt={partner}
                w="32px"
                h="32px"
                objectFit="contain"
                borderRadius="md"
              />
            )}
            <Heading20>
              {isConnected && id ? (
                <LinkOverlay as={Link} to={generatePath(PEOPLE_TEAM_ROUTES.company.integrations.show, { id })}>
                  {name}
                </LinkOverlay>
              ) : (
                name
              )}
            </Heading20>
          </HStack>
          {showSyncChannels && (
            <SyncChannelsButton />
          )}
        </Flex>

        <Flex direction="column" flex={1} mb={4}>
          {isConnected ? (
            <>
              {createdAt && (
                <Flex justifyContent="space-between" alignItems="center" mb={1}>
                  <CDS.Text10 color="fg.muted">
                    Added:
                  </CDS.Text10>
                  <CDS.Text12 color="fg.default" pr={32}>
                    {timeAtDayMonth(createdAt)}
                  </CDS.Text12>
                </Flex>
              )}
              {updatedAt && (
                <Flex justifyContent="space-between" alignItems="center">
                  <CDS.Text10 color="fg.muted">
                    Last updated:
                  </CDS.Text10>
                  <CDS.Text12 color="fg.default" pr={32}>
                    {timeAtDayMonth(updatedAt)}
                  </CDS.Text12>
                </Flex>
              )}
            </>
          ) : (
            <CDS.Text12 color="fg.muted">
              {description}
            </CDS.Text12>
          )}
        </Flex>

        <Flex direction="column">
          <Divider mb={4} />

          {isConnected ? (
            <Flex alignItems="center" justifyContent="space-between">
              <Tag
                size="lg"
                fontSize="sm"
                {...statusStyle}
              >
                {statusLabel}
              </Tag>

              <HStack spacing={1}>
                {needsReconnect && (
                  <TertiaryButton size="sm" onClick={handleReconnect}>
                    Reconnect
                  </TertiaryButton>
                )}

                {showPauseButton && (
                  <IconButton
                    aria-label={isActive ? "Pause Integration" : "Resume Integration"}
                    icon={isActive ? <IoMdPause /> : <IoMdPlay />}
                    size="sm"
                    variant="ghost"
                    onClick={toggleIsActive}
                  />
                )}

                {includeDelete && (
                  <ButtonWithConfirm {...CONFIRM_DELETE} intent="warning" handleConfirm={handleDelete}>
                    <IconButton
                      aria-label="Delete Integration"
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                    />
                  </ButtonWithConfirm>
                )}
              </HStack>
            </Flex>
          ) : (
            <TertiaryButton
              onClick={onConnect}
              size="sm"
              height="100%"
              px={6}
              py={1}
              alignSelf="flex-start"
            >
              {connectButtonText}
            </TertiaryButton>
          )}
        </Flex>
      </CardBody>
    </LinkBox>
  );
}
